/**
 * Pure calculation for a single mersalg (upsell) container's totals + margin.
 * No DB or React dependencies so it can be unit-tested directly.
 *
 * Extracted verbatim from UpsellEditDialog (WP10 GJELD-2). The kunde-vendt
 * total (lineSum/overhead/rabatt/total) speiler `calcUpsellExportTotals` i
 * `upsell-export.ts`; her beregnes i tillegg `totalMargin` (intern BF-impact),
 * som eksporten bevisst utelater.
 *
 * Business rules (uendret):
 * - Interne time-linjer (cost_type='internal', unit='t'|'d'): margin = amount
 *   (lønn/overhead dekkes av amount, ingen separat innkjøp).
 * - Øvrige linjer: margin = amount − estimated_cost.
 * - Effektiv overhead-prosent = upsell.overhead_pct ?? defaultOverheadPct.
 * - Overhead-beløp = lineSum × effektiv overhead / 100.
 * - Fast rabatt-beløp (discount_amount > 0) overstyrer prosent-rabatt.
 * - Prosent-rabatt regnes på (lineSum + overhead).
 * - Total = lineSum + overhead − effektiv rabatt.
 * - totalMargin = sum(linjemargin) + overhead − effektiv rabatt.
 */

export interface UpsellTotalsLine {
  amount: number | null;
  estimated_cost: number | null;
  cost_type: string | null;
  unit: string | null;
}

export interface UpsellTotalsInput {
  lines: UpsellTotalsLine[];
  /** upsells.overhead_pct — null = bruk prosjektets default. */
  overhead_pct: number | null;
  discount_pct: number | null;
  discount_amount: number | null;
  /** Prosjektets default overhead-prosent. */
  defaultOverheadPct: number;
}

export interface UpsellTotals {
  lineSum: number;
  effectiveOverheadPct: number;
  overheadAmount: number;
  effectiveDiscount: number;
  total: number;
  totalMargin: number;
}

/** Per-linje margin. Interne time-linjer teller hele beløpet som margin. */
export function upsellLineMargin(l: UpsellTotalsLine): number {
  const amount = l.amount ?? 0;
  const cost = l.estimated_cost ?? 0;
  const isHourly = l.cost_type === "internal" && (l.unit === "t" || l.unit === "d");
  if (isHourly) return amount;
  return amount - cost;
}

export function computeUpsellTotals(input: UpsellTotalsInput): UpsellTotals {
  const lineSum = input.lines.reduce((s, l) => s + (l.amount ?? 0), 0);
  const effectiveOverheadPct = input.overhead_pct ?? input.defaultOverheadPct;
  const overheadAmount = (lineSum * effectiveOverheadPct) / 100;
  const baseWithOverhead = lineSum + overheadAmount;
  const discountFromAmount = input.discount_amount ?? 0;
  const discountFromPct = input.discount_pct
    ? (baseWithOverhead * input.discount_pct) / 100
    : 0;
  const effectiveDiscount = discountFromAmount > 0 ? discountFromAmount : discountFromPct;
  const total = baseWithOverhead - effectiveDiscount;

  const totalMargin =
    input.lines.reduce((s, l) => s + upsellLineMargin(l), 0) +
    overheadAmount -
    effectiveDiscount;

  return {
    lineSum,
    effectiveOverheadPct,
    overheadAmount,
    effectiveDiscount,
    total,
    totalMargin,
  };
}

/**
 * VIB-994: T&M-mersalg har ingen fastsum å vise (amount_excl_vat holdes
 * alltid 0, jf. recomputeUpsellAmount) — "hittil fakturert" for et T&M-
 * mersalg er i stedet summen av `invoices.amount_excl_vat` for fakturaer
 * knyttet til mersalget via `invoices.upsell_id` (løpende regning, flere
 * fakturaer per mersalg — IKKE `added_to_invoice_id`-én-til-én-modellen som
 * fastpris bruker). Ren funksjon, ingen DB-avhengighet — MersalgTab henter
 * `invoices` fra `useProjectDetail`, som allerede er lastet der.
 */
export function computeTMInvoicedTotal(
  upsellId: string,
  invoices: { upsell_id?: string | null; amount_excl_vat: number | null; amount: number | null }[],
): number {
  return invoices
    .filter((i) => i.upsell_id === upsellId)
    .reduce((s, i) => s + Number(i.amount_excl_vat ?? i.amount ?? 0), 0);
}
